import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import FeedbackModal from "@/components/FeedbackModal";
import UpgradeModal from "@/components/UpgradeModal";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Mail, Sparkles, ShieldCheck, LogOut, MessageCircle,
  Activity, Target, TrendingUp, ArrowRight, ExternalLink, Save,
} from "lucide-react";

export default function Profile() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [quota, setQuota] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [username, setUsername] = useState("");
  const [publicOn, setPublicOn] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setUsername(user.username || "");
      setPublicOn(user.profile_public !== false);
    }
  }, [user]);

  useEffect(() => {
    (async () => {
      try {
        const [q, a, ad] = await Promise.all([
          api.get("/user/quota"),
          api.get("/attempts"),
          api.get("/admin/me"),
        ]);
        setQuota(q.data);
        setAttempts(a.data.attempts || []);
        setIsAdmin(!!ad.data.is_admin);
      } catch (err) {
        console.error("profile load failed:", err);
      }
    })();
  }, []);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.patch("/user/profile", { username, profile_public: publicOn });
      if (refresh) await refresh();
      toast.success("Profile updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  const graded = attempts.filter((a) => a.status === "graded");
  const totalScore = graded.reduce((s, a) => s + (a.analytics?.score || 0), 0);
  const totalMax = graded.reduce((s, a) => s + (a.analytics?.max_score || 0), 0);
  const avgAcc = graded.length
    ? Math.round(graded.reduce((s, a) => s + (a.analytics?.accuracy || 0), 0) / graded.length)
    : 0;

  const doLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/login");
  };

  if (!user) return null;

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto space-y-8 fade-up">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-2">Profile</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Your account</h1>
        </div>

        {/* Identity */}
        <Card className="p-8 rounded-md border-stone-200 shadow-none">
          <div className="flex items-start gap-6">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-stone-900 text-3xl font-heading font-bold text-white flex-shrink-0">
              {user.picture ? (
                <img src={user.picture} alt="" className="h-20 w-20 rounded-full object-cover" />
              ) : (
                user.name?.[0]?.toUpperCase() || "?"
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="font-heading text-2xl font-bold">{user.name}</h2>
                {isAdmin && (
                  <Badge className="bg-orange-600 hover:bg-orange-600 text-white font-mono text-[10px] uppercase tracking-[0.2em]" data-testid="admin-badge">
                    <ShieldCheck className="h-3 w-3 mr-1" />
                    Admin
                  </Badge>
                )}
                {quota?.is_premium ? (
                  <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white font-mono text-[10px] uppercase tracking-[0.2em]">
                    Premium
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="font-mono text-[10px] uppercase tracking-[0.2em]">
                    Free plan
                  </Badge>
                )}
              </div>
              <div className="mt-3 space-y-1 text-sm text-stone-600">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-stone-400" />
                  <span>{user.email}</span>
                </div>
                {user.username && (
                  <Link
                    to={`/u/${user.username}`}
                    className="inline-flex items-center gap-1 text-orange-600 hover:text-orange-700 text-sm mt-1"
                    data-testid="view-public-profile-link"
                  >
                    View public profile @{user.username}
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Public profile settings */}
        <Card className="p-6 rounded-md border-stone-200 shadow-none" data-testid="public-profile-settings">
          <div className="flex items-start justify-between mb-4 gap-4">
            <div>
              <h3 className="font-heading text-lg font-semibold">Public profile</h3>
              <p className="text-xs text-stone-500 mt-1">
                Show off your streak, best score, and recent activity at exambro.app/u/&lt;you&gt;. Great for coaching batches and study groups.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                {publicOn ? "Public" : "Private"}
              </span>
              <Switch
                checked={publicOn}
                onCheckedChange={setPublicOn}
                data-testid="profile-public-toggle"
              />
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-2">
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Username</Label>
              <div className="flex items-center rounded-md border border-stone-300 focus-within:border-orange-500 mt-1">
                <span className="pl-3 pr-1 text-sm text-stone-400 font-mono">exambro.app/u/</span>
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase())}
                  className="border-0 focus-visible:ring-0 font-mono"
                  placeholder="aakash-p"
                  data-testid="username-input"
                />
              </div>
              <p className="text-[11px] text-stone-500 mt-1">
                Lowercase letters, numbers, hyphens. Min 3 characters.
              </p>
            </div>
            <Button
              onClick={saveProfile}
              disabled={saving}
              className="bg-orange-600 hover:bg-orange-700 text-white h-10"
              data-testid="save-profile-btn"
            >
              <Save className="h-4 w-4 mr-1" /> Save
            </Button>
          </div>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Attempts" v={graded.length} icon={Activity} />
          <StatCard label="Avg accuracy" v={`${avgAcc}%`} icon={Target} accent="text-emerald-600" />
          <StatCard label="Points earned" v={Math.round(totalScore)} icon={TrendingUp} accent="text-orange-600" />
          <StatCard
            label="This month"
            v={quota ? (quota.is_premium ? "∞" : `${quota.used} / ${quota.limit}`) : "—"}
            icon={Sparkles}
          />
        </div>

        {/* Actions */}
        <Card className="p-6 rounded-md border-stone-200 shadow-none">
          <h3 className="font-heading text-xl font-semibold mb-4">Manage</h3>
          <div className="space-y-2">
            {isAdmin && (
              <ActionRow
                icon={ShieldCheck}
                title="Admin panel"
                desc="Waitlist, feedback inbox, top users, growth signals"
                cta="Open"
                accent="orange"
                onClick={() => navigate("/admin")}
                testid="profile-admin-btn"
              />
            )}
            {!quota?.is_premium && (
              <ActionRow
                icon={Sparkles}
                title="Upgrade to Premium"
                desc="Unlimited uploads, AI error analysis, replay timeline"
                cta="View plans"
                accent="orange"
                onClick={() => setShowUpgrade(true)}
                testid="profile-upgrade-btn"
              />
            )}
            <ActionRow
              icon={MessageCircle}
              title="Send feedback"
              desc="Bugs, feature ideas, kind words — read by the team"
              cta="Open"
              onClick={() => setShowFeedback(true)}
              testid="profile-feedback-btn"
            />
            <ActionRow
              icon={LogOut}
              title="Sign out"
              desc="End this session"
              cta="Logout"
              accent="rose"
              onClick={doLogout}
              testid="profile-logout-btn"
            />
          </div>
        </Card>
      </div>

      <FeedbackModal open={showFeedback} onOpenChange={setShowFeedback} />
      <UpgradeModal open={showUpgrade} onOpenChange={setShowUpgrade} quota={quota} />
    </AppLayout>
  );
}

function StatCard({ label, v, icon: Icon, accent = "text-stone-900" }) {
  return (
    <Card className="p-4 rounded-md border-stone-200 shadow-none">
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">{label}</span>
        <Icon className={`h-4 w-4 ${accent}`} />
      </div>
      <div className={`font-heading text-2xl font-bold tabular-nums ${accent}`}>{v}</div>
    </Card>
  );
}

function ActionRow({ icon: Icon, title, desc, cta, accent = "stone", onClick, testid }) {
  const cls = {
    stone: "bg-stone-100 text-stone-700",
    orange: "bg-orange-50 text-orange-600",
    rose: "bg-rose-50 text-rose-600",
  }[accent];
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="w-full flex items-center gap-4 p-4 rounded-md border border-stone-200 hover:border-orange-400 text-left group"
      style={{ transition: "border-color 0.2s ease" }}
    >
      <div className={`h-10 w-10 rounded-md ${cls} flex items-center justify-center flex-shrink-0`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium">{title}</div>
        <div className="text-xs text-stone-500 mt-0.5">{desc}</div>
      </div>
      <div className="flex items-center gap-2 text-sm font-medium text-stone-500 group-hover:text-orange-600">
        {cta}
        <ArrowRight className="h-4 w-4" />
      </div>
    </button>
  );
}
